// iter157 — Stock Adjustment dialog.
// Later-month-only correction mechanism so a physical rice count can
// reconcile without touching the chain-anchor opening. Writes to
// POST /fund-balances/stock-adjustment which appends to the fund doc's
// stockAdjustments array and bumps receivedRice.balVatika by `kg`.
//
// Not shown on the anchor month — the user is told to Modify Opening
// Balance instead (bigger CTA button on that month).

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { k: "physical_count", label: "Physical stock count" },
  { k: "spillage", label: "Spillage / damage" },
  { k: "transfer_in", label: "Transfer in from another school" },
  { k: "transfer_out", label: "Transfer out to another school" },
  { k: "other", label: "Other" },
];

export default function StockAdjustmentDialog({ fund, schoolId, year, month, isHardLocked, onClose, onSaved }) {
  const todayIso = (() => {
    const last = new Date(year, month, 0).getDate();
    const d = Math.min(new Date().getDate(), last);
    return `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  })();
  const [kg, setKg] = useState(0);
  const [reason, setReason] = useState("physical_count");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(todayIso);
  const [saving, setSaving] = useState(false);

  const lastDay = new Date(year, month, 0).getDate();
  const invalidDate = (() => {
    const [yy, mm, dd] = (date || "").split("-").map((v) => parseInt(v, 10));
    return !(yy === year && mm === month && dd >= 1 && dd <= lastDay);
  })();

  const save = async () => {
    if (isHardLocked) return toast.error("Month is hard-locked");
    if (invalidDate) return toast.error("Date must fall inside this month");
    if (!kg || Number(kg) === 0) return toast.error("kg cannot be zero");
    try {
      setSaving(true);
      await axios.post(`${API}/fund-balances/stock-adjustment`, {
        schoolId, year, month,
        date, kg: Number(kg), reason, note: note.trim(),
      });
      toast.success(`Stock adjusted by ${Number(kg) > 0 ? "+" : ""}${Number(kg).toFixed(3)} kg`);
      await onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save adjustment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-ink-900/60 p-4" data-testid="stock-adj-dialog">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-border bg-sand-50 px-5 py-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-ink-900">Stock Adjustment · ମହଜୁଦ ସଂଶୋଧନ</h2>
            <p className="text-xs text-ink-700">Use positive kg to add stock, negative to remove. Rice Report ledger shows the entry on {year}-{String(month).padStart(2, "0")}.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="stock-adj-close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        {isHardLocked && (
          <div className="flex items-center gap-2 border-b border-rose-200 bg-rose-50 px-5 py-2 text-xs font-semibold text-rose-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Month is hard-locked — read-only
          </div>
        )}
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-ink-700">Date</span>
              <input
                type="date"
                min={`${year}-${String(month).padStart(2, "0")}-01`}
                max={`${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={isHardLocked}
                data-testid="stock-adj-date"
                className="mt-1 h-10 w-full rounded border border-border bg-white px-3 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-ink-700">kg (± allowed)</span>
              <input
                type="number"
                step="0.001"
                value={kg}
                onChange={(e) => setKg(e.target.value)}
                disabled={isHardLocked}
                data-testid="stock-adj-kg"
                className="mt-1 h-10 w-full rounded border border-border bg-white px-3 text-right font-mono text-sm"
              />
            </label>
          </div>
          <div>
            <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-ink-700">Reason</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {REASONS.map((r) => (
                <button
                  key={r.k}
                  type="button"
                  disabled={isHardLocked}
                  onClick={() => setReason(r.k)}
                  data-testid={`stock-adj-reason-${r.k}`}
                  className={`rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors ${
                    reason === r.k ? "border-clay bg-clay text-white" : "border-slate-200 bg-white text-ink-800 hover:border-clay/40"
                  }`}
                >{r.label}</button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-ink-700">Note (optional)</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isHardLocked}
              placeholder="e.g., Counted 10 kg short vs ledger"
              data-testid="stock-adj-note"
              className="mt-1 h-10 w-full rounded border border-border bg-white px-3 text-sm"
            />
          </label>
          {Array.isArray(fund?.stockAdjustments) && fund.stockAdjustments.length > 0 && (
            <div className="rounded-lg border border-border bg-sand-50 p-3" data-testid="stock-adj-history">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-ink-700">Earlier adjustments this month</p>
              <ul className="mt-2 space-y-1 text-xs">
                {fund.stockAdjustments.slice(-6).map((a) => (
                  <li key={a.id} className="flex justify-between gap-3">
                    <span className="font-mono">{a.date}</span>
                    <span className={`font-mono ${a.kg >= 0 ? "text-moss-dark" : "text-rose-600"}`}>{a.kg >= 0 ? "+" : ""}{Number(a.kg).toFixed(3)} kg</span>
                    <span className="flex-1 text-right text-ink-700">{a.reason}{a.note ? ` — ${a.note}` : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border bg-sand-50 px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="stock-adj-cancel">Cancel</Button>
          <Button
            size="sm"
            disabled={isHardLocked || invalidDate || saving}
            onClick={save}
            className="bg-clay text-white hover:bg-clay-dark"
            data-testid="stock-adj-save"
          >
            <Save className="mr-1 h-4 w-4" />
            {saving ? "Saving…" : "Record adjustment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
